'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import { useParams, useRouter, useSearchParams } from 'next/navigation';

import { PercentageCard } from '@/components/molecules/PercentageCard';
import { StatsCard } from '@/components/molecules/StatsCard';
import { Stats, StatsFormatType } from '@/entities/player-details/Stats';
import { useSeasonDistrictDivisionInfo } from '@/hooks/custom-hooks/useSeasonDistrictDivisionInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { usePlayerProfileStatsQuery } from '@/hooks/player-details/player-profile-stats';
import { formatTeamLeagueLabels, truncateToThreeDecimals } from '@/utils/helpers';

export const PlayerStatsDashboard = () => {
	const { showPlayerRatingsSingles, showPlayerRatingsDoubles, showPlayerRatingsSkinnySingles } = useTeamLeagueInfo();

	return (
		<div>
			<SeasonFilter />
			<PlayerStats
				showRatings={{
					singles: showPlayerRatingsSingles,
					doubles: showPlayerRatingsDoubles,
					skinnySingles: showPlayerRatingsSkinnySingles
				}}
			/>
		</div>
	);
};

const SeasonFilter = () => {
	const searchParams = useSearchParams();
	const router = useRouter();
	const { seasonOptions } = useSeasonDistrictDivisionInfo();

	const handleFilterChange = (season: string) => {
		const params = new URLSearchParams(searchParams.toString());

		if (season !== 'all') {
			params.set('season', season);
		} else {
			params.delete('season');
		}

		router.replace(`?${params.toString()}`, { scroll: false });
	};

	const modifiedSeasonOptions = seasonOptions.length ? [{ label: 'Career', value: 'all' }, ...seasonOptions] : [];
	const selectedSeason = searchParams.get('season') || 'all';

	return (
		<div className="w-full md:w-1/2 mb-4 md:mb-6 flex flex-col gap-1">
			<p className="text-sm font-medium leading-5 text-gray-700">Select season</p>
			<Select onValueChange={(value) => handleFilterChange(value)} value={selectedSeason || modifiedSeasonOptions[0]?.value}>
				<SelectTrigger
					className="bg-white focus:ring-0 h-10 !max-w-[630px]"
					icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
				>
					<SelectValue placeholder="Select season" />
				</SelectTrigger>
				<SelectContent>
					{modifiedSeasonOptions.map((season) => (
						<SelectItem key={season.value} value={season.value}>
							{season.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
};

interface ShowRatingsProps {
	showRatings: {
		singles: boolean;
		doubles: boolean;
		skinnySingles: boolean;
	};
}

const PlayerStats = ({ showRatings }: ShowRatingsProps) => {
	const params = useParams<{
		teamLeagueId: string;
		playerId: string;
	}>();

	const searchParams = useSearchParams();
	const selectedSeason = searchParams.get('season');
	const { data: playerProfileStats, isLoading: isLoadingPlayerProfileStats } = usePlayerProfileStatsQuery(
		params.teamLeagueId,
		params.playerId,
		selectedSeason
	);

	if (isLoadingPlayerProfileStats) {
		return <PlayerStatsDashboardSkeleton />;
	}

	if (playerProfileStats?.statusCode !== 200) {
		throw new Error(playerProfileStats?.error);
	}

	const overall = playerProfileStats?.data.stats.find((stats) => stats.formatType === StatsFormatType.OVERALL);
	const doubles = playerProfileStats?.data.stats.find((stats) => stats.formatType === StatsFormatType.DOUBLES);
	const mixed = playerProfileStats?.data.stats.find((stats) => stats.formatType === StatsFormatType.MIXED);
	const singles = playerProfileStats?.data.stats.find((stats) => stats.formatType === StatsFormatType.SINGLES);
	const skinnySingles = playerProfileStats?.data.stats.find((stats) => stats.formatType === StatsFormatType.SKINNY_SINGLES);

	return (
		<div className="flex flex-col gap-6">
			<PlayerStatsPerFormat stats={overall} title="Overall" />

			{showRatings.doubles ? <PlayerStatsPerFormat stats={doubles} title="Gender Doubles" /> : null}

			<PlayerStatsPerFormat stats={mixed} title="Mixed Doubles" />

			{showRatings.singles ? <PlayerStatsPerFormat stats={singles} title="Singles" /> : null}

			{showRatings.skinnySingles ? <PlayerStatsPerFormat stats={skinnySingles} title="Skinny Singles" /> : null}
		</div>
	);
};

const PlayerStatsPerFormat = ({ stats, title }: { stats?: Stats; title: string }) => {
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	return (
		<div className="flex flex-col gap-4">
			<p className="font-bold text-gray-900 text-lg">{title}</p>
			<div>
				<StatsCard
					columns={[
						{
							header: 'Matches',
							type: 'simple-stats',
							changeIndicator: {
								changePercentage: stats?.matchWinsPctChange ?? 0,
								isPositive: Number(stats?.matchWinsPctChange) > 0
							},
							data: {
								headers: ['W', 'L', 'Diff'],
								values: [stats?.matchWins ?? 0, stats?.matchLosses ?? 0, stats?.matchDiff ?? 0]
							}
						},
						{
							header: 'Games',
							type: 'simple-stats',
							changeIndicator: { changePercentage: stats?.gameWinsPctChange ?? 0, isPositive: Number(stats?.gameWinsPct) > 0 },
							data: {
								headers: ['W', 'L', 'Diff'],
								values: [stats?.gameWins ?? 0, stats?.gameLosses ?? 0, stats?.gameDiff ?? 0]
							}
						},
						{
							header: 'Points',
							type: 'simple-stats',
							changeIndicator: {
								changePercentage: stats?.pointsEarnedPctChange ?? 0,
								isPositive: Number(stats?.pointsEarnedPctChange) > 0
							},
							data: {
								headers: ['E', 'A', 'Diff', 'PD %'],
								values: [stats?.pointsEarned ?? 0, stats?.pointsAgainst ?? 0, stats?.pointsDiff ?? 0, stats?.pointsDiffPct ?? 0]
							}
						},
						{
							header: `${labels.tieBreakerMatchAbbreviation} Points`,
							type: 'simple-stats',
							changeIndicator: {
								changePercentage: stats?.tbPointsEarnedPctChange ?? 0,
								isPositive: Number(stats?.tbPointsEarnedPctChange) > 0
							},
							data: {
								headers: ['E', 'A', 'Diff', 'PD %'],
								values: [
									stats?.tbPointsEarned ?? 0,
									stats?.tbPointsAgainst ?? 0,
									stats?.tbPointsDiff ?? 0,
									stats?.tbPointsDiffPct ?? 0
								]
							}
						},
						{
							header: 'Avg ratings',
							type: 'simple-stats',
							data: {
								headers: ['Partner', 'Opponent'],
								values: [
									stats?.avgPartnerRating != null ? truncateToThreeDecimals(stats.avgPartnerRating) : 0,
									stats?.avgOpponentRating != null ? truncateToThreeDecimals(stats.avgOpponentRating) : 0
								]
							}
						}
					]}
				/>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<PercentageCard title={'Match Win %'} percentage={stats?.matchWinsPct ?? 0} changePercentage={stats?.matchWinsPctChange ?? 0} />
				<PercentageCard title={'Game Win %'} percentage={stats?.gameWinsPct ?? 0} changePercentage={stats?.gameWinsPctChange ?? 0} />
				<PercentageCard
					title={'Points Earned %'}
					percentage={stats?.pointsEarnedPct ?? 0}
					changePercentage={stats?.pointsEarnedPctChange ?? 0}
				/>
			</div>
		</div>
	);
};

export const PlayerStatsDashboardSkeleton = () => {
	return (
		<div className="flex flex-col gap-6">
			{[...Array(3)].map((_, i) => (
				<div key={i}>
					<div className="mb-4">
						<div className="w-full h-40 bg-gray-200 rounded-lg animate-pulse" />
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
						<div className="h-32 bg-gray-200 rounded-lg animate-pulse" />
						<div className="h-32 bg-gray-200 rounded-lg animate-pulse" />
						<div className="h-32 bg-gray-200 rounded-lg animate-pulse" />
					</div>
				</div>
			))}
		</div>
	);
};
